/**
 * Situação do CNPJ na Receita Federal (ADR 0068). O servidor consulta a BrasilAPI (dados abertos
 * da Receita, gratuita) e devolve só o que o cadastro usa: nada do quadro de sócios, que é dado
 * pessoal. CPF nunca é consultado fora.
 */
import { formatCep } from './address.ts';

export const REGISTRY_STATUSES = [
  'active',
  'suspended',
  'unfit',
  'closed',
  'void',
  'not_found',
] as const;
export type RegistryStatus = (typeof REGISTRY_STATUSES)[number];

/** Códigos da situação cadastral da Receita. */
const STATUS_BY_CODE: Record<number, RegistryStatus> = {
  1: 'void',
  2: 'active',
  3: 'suspended',
  4: 'unfit',
  8: 'closed',
};

export interface CnpjRegistryData {
  cnpj: string;
  status: RegistryStatus;
  /** Data da situação atual (AAAA-MM-DD). */
  statusDate: string | null;
  legalName: string;
  tradeName: string | null;
  openedOn: string | null;
  mainActivity: string | null;
  email: string | null;
  phone: string | null;
  address: {
    postalCode: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    district: string | null;
    city: string | null;
    region: string | null;
  };
}

/** A consulta: os dados, CNPJ inexistente na Receita, ou consulta indisponível. */
export type CnpjRegistryResult =
  { found: true; data: CnpjRegistryData } | { found: false; reason: 'not_found' | 'unavailable' };

/** CNPJ que não está ativo (ou não existe na Receita) exige justificativa no cadastro. */
export function registryNeedsNote(status: RegistryStatus | null | undefined): boolean {
  return status != null && status !== 'active';
}

const text = (v: unknown, max: number): string | null => {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const s = String(v).replace(/\s+/g, ' ').trim();
  return s ? s.slice(0, max) : null;
};
const isoDate = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);

/** Telefone da Receita (DDD + número, só algarismos) no formato (11) 3456-7890. */
function phone(v: unknown): string | null {
  const d = typeof v === 'string' ? v.replace(/\D/g, '') : '';
  if (d.length < 10 || d.length > 11) return null;
  return `(${d.slice(0, 2)}) ${d.slice(2, -4)}-${d.slice(-4)}`;
}

/** Resposta da BrasilAPI (/api/cnpj/v1) no formato do cadastro, ou null se não reconhecer. */
export function parseBrasilApiCnpj(raw: unknown): CnpjRegistryData | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const legalName = text(r.razao_social, 300);
  const cnpj = text(r.cnpj, 14)?.toUpperCase();
  const status =
    STATUS_BY_CODE[Number(r.situacao_cadastral)] ??
    ({ ATIVA: 'active', SUSPENSA: 'suspended', INAPTA: 'unfit', BAIXADA: 'closed', NULA: 'void' }[
      String(r.descricao_situacao_cadastral ?? '').toUpperCase()
    ] as RegistryStatus | undefined);
  if (!legalName || !cnpj || !status) return null;
  // A Receita separa o tipo do logradouro ("AVENIDA") do nome ("PAULISTA").
  const kind = text(r.descricao_tipo_de_logradouro, 50);
  const name = text(r.logradouro, 200);
  const street =
    kind && name && !name.toUpperCase().startsWith(kind.toUpperCase()) ? `${kind} ${name}` : name;
  const cep = text(r.cep, 8);
  const uf = text(r.uf, 2)?.toUpperCase() ?? null;
  const cnae = text(r.cnae_fiscal, 7);
  const activity = text(r.cnae_fiscal_descricao, 300);
  const email = text(r.email, 254)?.toLowerCase() ?? null;
  return {
    cnpj,
    status,
    statusDate: isoDate(r.data_situacao_cadastral),
    legalName,
    tradeName: text(r.nome_fantasia, 300),
    openedOn: isoDate(r.data_inicio_atividade),
    mainActivity: activity ? (cnae ? `${cnae} · ${activity}` : activity) : null,
    email: email && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? email : null,
    phone: phone(r.ddd_telefone_1),
    address: {
      postalCode: cep && /^\d{8}$/.test(cep) ? formatCep(cep) : null,
      street: street?.slice(0, 200) ?? null,
      number: text(r.numero, 20),
      complement: text(r.complemento, 100),
      district: text(r.bairro, 100),
      city: text(r.municipio, 100),
      region: uf && /^[A-Z]{2}$/.test(uf) ? uf : null,
    },
  };
}
